/**
 * /app/{tenantSlug}/loans/{loanId} — Loan detail (migration 064).
 *
 * Header (principal / outstanding / status), the installment schedule
 * table, the append-only event ledger, and the finance action buttons
 * (approve / reject / disburse / prepay / write-off). Disburse + write-off
 * are STRICT step-up-TOTP server-side — the client.ts 401 totp_required
 * short-circuit drives the TOTP modal + retry transparently, so no special
 * handling is needed here beyond surfacing the eventual error/success.
 *
 * §17(2): the detail surfaces the interest-free-over-₹20k perquisite
 * disclaimer (the backend computes the flag; v1 does not value it).
 */

import { useState } from "react";
import { createFileRoute, Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { toApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { isAccountingAdmin } from "@/lib/nav";
import {
  useLoan,
  useApproveLoan,
  useRejectLoan,
  useCancelLoan,
  useDisburseLoan,
  usePrepayLoan,
  useWriteOffLoan,
} from "@/hooks/use-loans";
import { formatPaise, rupeesToPaise } from "@/lib/expense-format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  loanStatusVariant,
  formatDuePeriod,
} from "./app.$tenantSlug.loans.index";

export const Route = createFileRoute(
  "/_authgated/app/$tenantSlug/loans/$loanId",
)({
  component: LoanDetailRoute,
});

function LoanDetailRoute() {
  const { tenantSlug, loanId } = useParams({ strict: false }) as {
    tenantSlug: string;
    loanId: string;
  };
  const id = Number(loanId);
  const { user } = useAuth();
  const finance = isAccountingAdmin(user?.role);

  const { data, isLoading } = useLoan(id);
  const approve = useApproveLoan();
  const reject = useRejectLoan();
  const cancel = useCancelLoan();
  const disburse = useDisburseLoan();
  const prepay = usePrepayLoan();
  const writeOff = useWriteOffLoan();

  const [rejectReason, setRejectReason] = useState("");
  const [prepayStr, setPrepayStr] = useState("");
  const [writeOffNote, setWriteOffNote] = useState("");

  if (isLoading || !data) {
    return <p className="p-6 text-sm text-muted-foreground">Loading…</p>;
  }
  const { loan, installments, events, perquisite_disclaimer } = data;

  async function run(p: Promise<unknown>, ok: string) {
    try {
      await p;
      toast.success(ok);
    } catch (err) {
      toast.error(toApiError(err).message);
    }
  }

  const isOwner = user?.user_id === loan.employee_id;

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Loan #{loan.id}
        </h1>
        <Button asChild variant="outline" className="gap-1.5">
          <Link to="/app/$tenantSlug/loans" params={{ tenantSlug }}>
            <ArrowLeft aria-hidden className="h-4 w-4" />
            Back
          </Link>
        </Button>
      </div>

      {perquisite_disclaimer && (
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            This interest-free loan exceeds ₹20,000 and may attract a §17(2)
            perquisite (imputed at SBI rates). Informational only — the
            platform does not value the perquisite in v1.
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="flex flex-wrap items-center gap-8 pt-6">
          <Stat label="Employee" value={loan.employee_name || `#${loan.employee_id}`} />
          <Stat label="Kind" value={loan.kind} />
          <Stat label="Principal" value={formatPaise(loan.principal_paise)} />
          <Stat label="Outstanding" value={formatPaise(loan.outstanding_paise)} />
          <Stat label="Tenure" value={`${loan.tenure_months} months`} />
          <div>
            <div className="text-xs uppercase text-muted-foreground">Status</div>
            <Badge variant={loanStatusVariant(loan.status)} className="capitalize">
              {loan.status.replace("_", " ")}
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-wrap gap-2">
        {isOwner && loan.status === "requested" && (
          <Button
            variant="outline"
            disabled={cancel.isPending}
            onClick={() => run(cancel.mutateAsync(loan.id), "Loan cancelled")}
          >
            Cancel request
          </Button>
        )}
        {finance && loan.status === "requested" && (
          <>
            <Button
              disabled={approve.isPending}
              onClick={() => run(approve.mutateAsync(loan.id), "Loan approved")}
            >
              Approve
            </Button>
            <RejectDialog
              pending={reject.isPending}
              reason={rejectReason}
              setReason={setRejectReason}
              onConfirm={() =>
                run(
                  reject.mutateAsync({ id: loan.id, reason: rejectReason }),
                  "Loan rejected",
                )
              }
            />
          </>
        )}
        {finance && loan.status === "approved" && (
          <Button
            disabled={disburse.isPending}
            onClick={() =>
              run(disburse.mutateAsync(loan.id), "Loan disbursed + posted to books")
            }
            className="gap-1.5"
          >
            {disburse.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            Disburse (TOTP)
          </Button>
        )}
        {finance && loan.status === "disbursed" && (
          <>
            <PrepayDialog
              pending={prepay.isPending}
              amount={prepayStr}
              setAmount={setPrepayStr}
              onConfirm={() => {
                const paise = rupeesToPaise(prepayStr);
                if (paise == null || paise <= 0) {
                  toast.error("Enter a valid prepayment amount");
                  return;
                }
                void run(
                  prepay.mutateAsync({ id: loan.id, amountPaise: paise }),
                  "Prepayment recorded + posted",
                );
              }}
            />
            <WriteOffDialog
              pending={writeOff.isPending}
              note={writeOffNote}
              setNote={setWriteOffNote}
              onConfirm={() =>
                run(
                  writeOff.mutateAsync({ id: loan.id, note: writeOffNote }),
                  "Loan written off + posted",
                )
              }
            />
          </>
        )}
      </div>

      {/* Schedule */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">EMI schedule</CardTitle>
        </CardHeader>
        <CardContent>
          {installments.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No schedule yet — generated on disbursement.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Due</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Outstanding after</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {installments.map((i) => (
                  <TableRow key={i.id}>
                    <TableCell className="font-mono">{i.seq_no}</TableCell>
                    <TableCell>{formatDuePeriod(i.due_year, i.due_month)}</TableCell>
                    <TableCell className="text-right">
                      {formatPaise(i.amount_paise)}
                    </TableCell>
                    <TableCell className="text-right">
                      {formatPaise(i.outstanding_after_paise)}
                    </TableCell>
                    <TableCell className="capitalize">{i.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Event ledger */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Ledger</CardTitle>
        </CardHeader>
        <CardContent>
          {events.length === 0 ? (
            <p className="text-sm text-muted-foreground">No events yet.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {events.map((e) => (
                <li
                  key={e.id}
                  className="flex justify-between border-b py-1 last:border-0"
                >
                  <span className="capitalize text-muted-foreground">
                    {e.kind.replace("_", " ")}
                    {e.note ? ` — ${e.note}` : ""}
                  </span>
                  <span className="font-medium">
                    {formatPaise(e.amount_paise)} · bal{" "}
                    {formatPaise(e.outstanding_after_paise)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-xs uppercase text-muted-foreground">{label}</div>
      <div className="text-lg font-semibold capitalize">{value}</div>
    </div>
  );
}

function RejectDialog({
  pending,
  reason,
  setReason,
  onConfirm,
}: {
  pending: boolean;
  reason: string;
  setReason: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Reject</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Reject loan</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="reject-reason">Reason (required)</Label>
          <Textarea
            id="reject-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
        <DialogFooter>
          <Button
            disabled={pending || reason.trim() === ""}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            Reject
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PrepayDialog({
  pending,
  amount,
  setAmount,
  onConfirm,
}: {
  pending: boolean;
  amount: string;
  setAmount: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Record prepayment</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Record prepayment</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="prepay-amount">Amount (₹)</Label>
          <Input
            id="prepay-amount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Posts DR Bank / CR Employee Loan Receivable and re-amortises the
            remaining schedule (tenure shortens).
          </p>
        </div>
        <DialogFooter>
          <Button
            disabled={pending}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            Record
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WriteOffDialog({
  pending,
  note,
  setNote,
  onConfirm,
}: {
  pending: boolean;
  note: string;
  setNote: (s: string) => void;
  onConfirm: () => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="destructive">Write off (TOTP)</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Write off remaining balance</DialogTitle>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="writeoff-note">Note (optional)</Label>
          <Textarea
            id="writeoff-note"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="text-xs text-muted-foreground">
            Irreversible. Posts DR Office Expenses / CR Employee Loan
            Receivable for the outstanding balance. Requires step-up TOTP.
          </p>
        </div>
        <DialogFooter>
          <Button
            variant="destructive"
            disabled={pending}
            onClick={() => {
              onConfirm();
              setOpen(false);
            }}
          >
            Write off
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
